import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Skeleton,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import companyApi from "../api/companyApi";
import useAuth from "../hooks/useAuth";
import useResourceManagement from "../hooks/useResourceManagement";
import useProductionManagement from "../hooks/useProductionManagement";
import SummaryTab from "../components/companies/SummaryTab";
import AssetDialogs from "../components/companies/AssetDialogs";
import ResourceSection from "../components/companies/ResourceSection";
import ForecastSection from "../components/companies/ForecastSection";
import ResourceDialogs from "../components/companies/ResourceDialogs";
import ProductionDialogs from "../components/companies/ProductionDialogs";
import {
  CLASSIFICATION_OPTIONS,
  getLifecycleLabel,
  getLifecycleStageLabel,
} from "../components/companies/resourceUtils";
import { CURRENCY_OPTIONS } from "../components/companies/productionUtils";
import { formatNumber, formatNumberWithCommas } from "../utils/numberUtils";
import { formatJurisdictionLabel } from "../utils/companiesFormatting";
import { formatLastUpdated, getStatusChipProps } from "../components/companies/statusUtils";
import { mergeReport } from "../utils/mergeUtils";

const actionChipSx = {
  ml: 1,
  px: 1,
  py: 0.2,
  borderRadius: 999,
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.01em",
  display: "inline-flex",
  alignItems: "center",
};

export default function CompanyProfileManagementPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState(0);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState("");
  const [forecasts, setForecasts] = useState([]);
  const [forecastsLoading, setForecastsLoading] = useState(false);
  const [forecastsFull, setForecastsFull] = useState(false);
  const [assetDialogRequest, setAssetDialogRequest] = useState(null);
  const [assetDeleteRequest, setAssetDeleteRequest] = useState(null);
  const [deleteFeedback, setDeleteFeedback] = useState(null);
  const [resourceReservesByAsset, setResourceReservesByAsset] = useState({});
  const [resourceReportsByReserve, setResourceReportsByReserve] = useState({});
  const [productionReportsByReserve, setProductionReportsByReserve] = useState({});
  const [commodities, setCommodities] = useState([]);
  const [productionMetrics, setProductionMetrics] = useState([]);
  const mergeAsset = useCallback((asset) => {
    if (!asset?.id) return;
    setAssets((prev) => {
      const next = [...prev];
      const idx = next.findIndex((item) => item.id === asset.id);
      if (idx >= 0) {
        next[idx] = { ...next[idx], ...asset };
      } else {
        next.push(asset);
      }
      return next;
    });
    setResourceReservesByAsset((prev) => ({
      ...prev,
      [asset.id]: prev[asset.id] || asset.resource_reserves || [],
    }));
    const reserveReports = {};
    (asset.resource_reserves || []).forEach((reserve) => {
      reserveReports[reserve.id] = reserve.production_reports || [];
    });
    setProductionReportsByReserve((prev) => ({
      ...prev,
      ...reserveReports,
    }));
  }, []);

  const removeAsset = useCallback((assetId) => {
    if (!assetId) return;
    setAssets((prev) => prev.filter((asset) => asset.id !== assetId));
    setResourceReservesByAsset((prev) => {
      const next = { ...prev };
      delete next[assetId];
      return next;
    });
    const reserveIds = (resourceReservesByAsset[assetId] || []).map((reserve) => reserve.id);
    setProductionReportsByReserve((prev) => {
      const next = { ...prev };
      reserveIds.forEach((reserveId) => {
        delete next[reserveId];
      });
      return next;
    });
  }, [resourceReservesByAsset]);

  const mergeResourceReserve = useCallback((reserve) => {
    const assetId = reserve?.asset?.id || reserve?.asset_id || reserve?.asset;
    if (!assetId || !reserve?.id) return;
    setResourceReservesByAsset((prev) => {
      const existing = prev[assetId] || [];
      const next = [...existing];
      const idx = next.findIndex((item) => item.id === reserve.id);
      if (idx >= 0) {
        next[idx] = { ...next[idx], ...reserve };
      } else {
        next.push(reserve);
      }
      return { ...prev, [assetId]: next };
    });
    setProductionReportsByReserve((prev) => ({
      ...prev,
      [reserve.id]: prev[reserve.id] || reserve.production_reports || [],
    }));
  }, []);

  const mergeResourceReport = useCallback((reserveId, report, values) => {
    if (!reserveId || !report?.id) return;
    setResourceReportsByReserve((prev) => {
      const existing = prev[reserveId] || [];
      const next = [...existing];
      const idx = next.findIndex((item) => item.id === report.id);
      const mergedReport = mergeReport(idx >= 0 ? next[idx] : null, report, values);
      if (idx >= 0) {
        next[idx] = mergedReport;
      } else {
        next.push(mergedReport);
      }
      return { ...prev, [reserveId]: next };
    });
  }, []);

  const removeResourceReport = useCallback((reserveId, reportId) => {
    if (!reserveId || !reportId) return;
    setResourceReportsByReserve((prev) => {
      const next = { ...prev };
      next[reserveId] = (next[reserveId] || []).filter((item) => item.id !== reportId);
      return next;
    });
  }, []);

  const resourceReportSourceOptions = useMemo(() => {
    const titles = new Set();
    const urls = new Set();
    Object.values(resourceReportsByReserve || {}).forEach((reports) => {
      (reports || []).forEach((report) => {
        const title = report?.source_title?.trim?.();
        const url = report?.source_url?.trim?.();
        if (title) titles.add(title);
        if (url) urls.add(url);
      });
    });
    return { titles: Array.from(titles), urls: Array.from(urls) };
  }, [resourceReportsByReserve]);

  const productionReportSourceOptions = useMemo(() => {
    const titles = new Set();
    const urls = new Set();
    Object.values(productionReportsByReserve || {}).forEach((reports) => {
      (reports || []).forEach((report) => {
        const title = report?.source_title?.trim?.();
        const url = report?.source_url?.trim?.();
        if (title) titles.add(title);
        if (url) urls.add(url);
      });
    });
    return { titles: Array.from(titles), urls: Array.from(urls) };
  }, [productionReportsByReserve]);

  const removeResourceReserve = useCallback(
    (reserveId, assetId) => {
      if (!reserveId) return;
      const resolvedAssetId =
        assetId ||
        Object.entries(resourceReservesByAsset || {}).find(([, reserves]) =>
          (reserves || []).some((reserve) => reserve.id === reserveId)
        )?.[0];
      if (!resolvedAssetId) return;
      setResourceReservesByAsset((prev) => {
        const next = { ...prev };
        next[resolvedAssetId] = (next[resolvedAssetId] || []).filter(
          (reserve) => reserve.id !== reserveId
        );
        return next;
      });
      setResourceReportsByReserve((prev) => {
        const next = { ...prev };
        delete next[reserveId];
        return next;
      });
      setProductionReportsByReserve((prev) => {
        const next = { ...prev };
        delete next[reserveId];
        return next;
      });
    },
    [resourceReservesByAsset]
  );

  const mergeProductionReport = useCallback((reserveId, report, values) => {
    if (!reserveId || !report?.id) return;
    setProductionReportsByReserve((prev) => {
      const existing = prev[reserveId] || [];
      const next = [...existing];
      const idx = next.findIndex((item) => item.id === report.id);
      const mergedReport = mergeReport(idx >= 0 ? next[idx] : null, report, values);
      if (idx >= 0) {
        next[idx] = mergedReport;
      } else {
        next.push(mergedReport);
      }
      return { ...prev, [reserveId]: next };
    });
  }, []);

  const removeProductionReport = useCallback((reserveId, reportId) => {
    if (!reserveId || !reportId) return;
    setProductionReportsByReserve((prev) => {
      const next = { ...prev };
      next[reserveId] = (next[reserveId] || []).filter((item) => item.id !== reportId);
      return next;
    });
  }, []);

  const removeEconomicScenario = useCallback((scenarioId) => {
    if (!scenarioId) return;
    setResourceReportsByReserve((prev) => {
      const next = { ...prev };
      Object.entries(next).forEach(([reserveId, reports]) => {
        const updatedReports = (reports || []).map((report) => {
          if (!report?.economic_scenarios) return report;
          const filtered = report.economic_scenarios.filter(
            (scenario) => scenario.id !== scenarioId
          );
          if (filtered.length === report.economic_scenarios.length) {
            return report;
          }
          return { ...report, economic_scenarios: filtered };
        });
        next[reserveId] = updatedReports;
      });
      return next;
    });
  }, []);

  const loadCompany = useCallback(async () => {
    setAssetsLoading(true);
    setAssetsError("");
    try {
      const data = await companyApi.getCompanyManageOverview(slug, {
        include: "assets,reference_data,submission_state,forecasts",
      });
      if (data?.company) {
        setCompany(data.company);
      }
      if (data?.submission_state) {
        setSubmissionState(data.submission_state);
      }
      if (data?.claims_summary) {
        setUserApprovedClaim(data.claims_summary.user_claim_status === "approved");
      }
      if (data?.reference_data) {
        setCommodities(data.reference_data.commodities || []);
        setProductionMetrics(data.reference_data.production_metrics || []);
      }
      if (data?.assets_nested) {
        const results = data.assets_nested || [];
        setAssets(results);
        const reservesByAsset = {};
        const reportsByReserve = {};
        const productionByReserve = {};
        results.forEach((asset) => {
          reservesByAsset[asset.id] = asset.resource_reserves || [];
          (asset.resource_reserves || []).forEach((reserve) => {
            reportsByReserve[reserve.id] = reserve.reports || [];
            productionByReserve[reserve.id] = reserve.production_reports || [];
          });
        });
        setResourceReservesByAsset(reservesByAsset);
        setResourceReportsByReserve(reportsByReserve);
        setProductionReportsByReserve(productionByReserve);
      }
      if (data?.forecasts) {
        setForecasts(data.forecasts || []);
        setForecastsFull(false);
      }
    } catch {
      setAssetsError("Unable to load assets.");
    } finally {
      setAssetsLoading(false);
    }
  }, [slug]);

  const loadForecastsFull = useCallback(async () => {
    setForecastsLoading(true);
    try {
      const data = await companyApi.getCompanyManageOverview(slug, {
        include: "forecasts_full",
      });
      if (data?.forecasts) {
        setForecasts(data.forecasts || []);
        setForecastsFull(true);
      }
    } catch {
      // keep existing forecasts if load fails
    } finally {
      setForecastsLoading(false);
    }
  }, [slug]);

  const loadSubmissionState = useCallback(async () => {
    try {
      const data = await companyApi.getCompanyManageOverview(slug, {
        include: "submission_state",
      });
      if (data?.submission_state) {
        setSubmissionState(data.submission_state);
      }
    } catch {
      // no-op: keep existing banner state if refresh fails
    }
  }, [slug]);

  const handleAssetSaved = useCallback(
    (asset) => {
      mergeAsset(asset);
      loadCompany();
    },
    [loadCompany, mergeAsset]
  );

  const handleAssetDeleted = useCallback(
    (assetId) => {
      removeAsset(assetId);
      loadCompany();
    },
    [loadCompany, removeAsset]
  );

  useEffect(() => {
    if (tab === 2 && !forecastsFull) {
      loadForecastsFull();
    }
  }, [tab, forecastsFull, loadForecastsFull]);

  const handleResourceReserveSaved = useCallback(
    (reserve) => {
      mergeResourceReserve(reserve);
      loadCompany();
    },
    [loadCompany, mergeResourceReserve]
  );

  const handleResourceReportSaved = useCallback(
    (reserveId, report, values) => {
      mergeResourceReport(reserveId, report, values);
      loadCompany();
    },
    [loadCompany, mergeResourceReport]
  );

  const handleResourceReportDeleted = useCallback(
    (reserveId, reportId) => {
      removeResourceReport(reserveId, reportId);
      loadCompany();
    },
    [loadCompany, removeResourceReport]
  );

  const handleResourceReserveDeleted = useCallback(
    (reserveId, assetId) => {
      removeResourceReserve(reserveId, assetId);
      loadCompany();
    },
    [loadCompany, removeResourceReserve]
  );

  const handleEconomicScenarioDeleted = useCallback(
    (scenarioId) => {
      removeEconomicScenario(scenarioId);
      loadCompany();
    },
    [loadCompany, removeEconomicScenario]
  );

  const handleProductionReportSaved = useCallback(
    (reserveId, report, values) => {
      mergeProductionReport(reserveId, report, values);
      loadCompany();
    },
    [loadCompany, mergeProductionReport]
  );

  const handleProductionReportDeleted = useCallback(
    (reserveId, reportId, report) => {
      if (report?.status === "delete_requested") {
        mergeProductionReport(reserveId, report);
      } else {
        removeProductionReport(reserveId, reportId);
      }
      loadCompany();
    },
    [loadCompany, mergeProductionReport, removeProductionReport]
  );

  const defaultReportingCurrency = company?.default_reporting_currency || "USD";

  const {
    addResourceReserveOpen,
    resourceReserveForm,
    resourceReserveSaveState,
    resourceReserveDialogMode,
    resourceReserveDeleteTarget,
    resourceReserveDeleteState,
    addResourceReportOpen,
    resourceReportForm,
    resourceReportSaveState,
    classificationTable,
    resourceReportDialogMode,
    resourceReportDeleteTarget,
    resourceReportDeleteState,
    resourceReportDuplicateNotice,
    economicScenarioSaveState,
    economicScenarioDeleteTarget,
    draftEconomicScenarios,
    stageOptions,
    setResourceReportDeleteTarget,
    setEconomicScenarioDeleteTarget,
    setResourceReserveDeleteTarget,
    handleResourceReserveFieldChange,
    handleResourceReportFieldChange,
    handleOpenAddResourceReserve,
    handleOpenEditResourceReserve,
    handleDuplicateResourceReserve,
    handleCloseResourceReserveDialog,
    handleOpenAddResourceReport,
    handleOpenEditResourceReport,
    handleDuplicateResourceReport,
    handleCloseResourceReportDialog,
    handleCommoditySelectionChange,
    handleClassificationRowChange,
    handleSubmitResourceReserve,
    handleUpdateResourceReserve,
    handleConfirmDeleteResourceReserve,
    handleSubmitResourceReport,
    handleUpdateResourceReport,
    handleConfirmDeleteResourceReport,
    handleOpenAddEconomicScenario,
    handleOpenEditEconomicScenario,
    handleConfirmDeleteEconomicScenario,
    handleDraftScenarioFieldChange,
    handleDraftScenarioPriceChange,
    handleAddDraftScenarioPriceRow,
    handleRemoveDraftScenarioPriceRow,
    handleRemoveDraftScenario,
  } = useResourceManagement({
    classificationOptions: CLASSIFICATION_OPTIONS,
    resourceReservesByAsset,
    onResourceReserveSaved: handleResourceReserveSaved,
    onResourceReserveDeleted: handleResourceReserveDeleted,
    onResourceReportSaved: handleResourceReportSaved,
    onResourceReportDeleted: handleResourceReportDeleted,
    onEconomicScenarioDeleted: handleEconomicScenarioDeleted,
    defaultReportingCurrency,
    assets,
  });
  const {
    addProductionReportOpen,
    productionReportForm,
    productionReportSaveState,
    productionMetricEntries,
    productionMetricOptions,
    showAddMetricSelector,
    productionReportDialogMode,
    productionReportDeleteTarget,
    productionReportDuplicateNotice,
    setProductionReportDeleteTarget,
    handleOpenAddProductionReport,
    handleDuplicateProductionReport,
    handleOpenEditProductionReport,
    handleCloseProductionReportDialog,
    handleProductionReportFieldChange,
    handleSelectProductionMetric,
    handleProductionMetricEntryChange,
    handleRemoveProductionMetric,
    handleToggleAddMetricSelector,
    submitProductionReport,
    updateProductionReport,
    handleConfirmDeleteProductionReport,
  } = useProductionManagement({
    productionMetrics,
    setDeleteFeedback,
    onProductionReportSaved: handleProductionReportSaved,
    onProductionReportDeleted: handleProductionReportDeleted,
    defaultReportingCurrency,
    assets,
    resourceReservesByAsset,
  });
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  const [submissionState, setSubmissionState] = useState(null);
  const [submissionReviewOpen, setSubmissionReviewOpen] = useState(false);
  const [submissionPayload, setSubmissionPayload] = useState(null);
  const [submissionActionLoading, setSubmissionActionLoading] = useState(false);
  const [submissionActionError, setSubmissionActionError] = useState(null);
  const [submissionCancelOpen, setSubmissionCancelOpen] = useState(false);
  const [settingsCurrency, setSettingsCurrency] = useState("USD");
  const [settingsSaveState, setSettingsSaveState] = useState({
    loading: false,
    error: null,
    success: null,
  });

  const [userApprovedClaim, setUserApprovedClaim] = useState(false);

  const isAdmin = user?.profile?.role === "admin" || user?.is_staff;
  const canEditCompany = isAdmin || userApprovedClaim;
  const companyId = company?.id;
  const draftState = submissionState?.draft_state;
  const openSubmission = submissionState?.open_submission;

  const canEditDrafts = canEditCompany && !openSubmission;
  const showSubmissionBanner = Boolean(
    canEditCompany && (draftState?.has_drafts || openSubmission)
  );

  const groupedSubmissionItems = useMemo(() => {
    const items = submissionPayload?.items || [];
    if (!items.length) return null;

    const fieldLabelMap = {
      name: "Name",
      asset_name: "Asset name",
      company_type: "Company type",
      company_lifecycle: "Lifecycle",
      company_lifecycle_stage: "Lifecycle stage",
      lifecycle: "Lifecycle",
      lifecycle_stage: "Lifecycle stage",
      ownership_percentage: "Ownership %",
      commodities: "Commodities",
      tickers_exchanges: "Tickers",
      corporate_structure: "Corporate structure",
      hq_country: "HQ country",
      hq_city: "HQ city",
      website_url: "Website",
      contact_email: "Contact email",
      contact_phone: "Contact phone",
      year_founded: "Year founded",
      corporate_description: "Description",
      recent_news_link: "Recent news link",
      corporate_presentation: "Corporate presentation",
      featured_video_link: "Featured video link",
      mining_method: "Mining method",
      source_title: "Source title",
      executive_name: "CEO name",
      background_summary: "Background summary",
      tenure_start_year: "Tenure start year",
      estimated_lower_bound_start_year: "Estimated lower-bound start year",
      estimated_upper_bound_start_year: "Estimated upper-bound start year",
      role_title: "Role title",
    };

    const entityLabels = {
      company_profile: "Company Profile",
      management_person: "Company Profile",
      asset: "Asset",
      resource_reserve: "Resources & Reserves",
      resource_reserve_report: "Resource Report",
      resource_reserve_value: "Resource Estimate",
      production_report: "Production Report",
      production_metric_value: "Production Metric",
      forecast: "Forecast",
    };
    const actionLabels = {
      create: "Created",
      update: "Updated",
      delete: "Deleted",
    };
    const hiddenFields = new Set([
      "id",
      "slug",
      "asset_id",
      "resource_reserve_id",
      "period_id",
      "last_updated_at",
    ]);
    const normalizeValue = (value) => {
      if (value === "" || value === null || value === undefined) return null;
      if (Array.isArray(value)) {
        const normalized = value.map((entry) => normalizeValue(entry));
        const isObjectArray = normalized.every(
          (entry) => entry && typeof entry === "object" && !Array.isArray(entry)
        );
        if (isObjectArray) {
          return normalized
            .map((entry) => ({
              sortKey: stableStringify(entry),
              value: entry,
            }))
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
            .map((entry) => entry.value);
        }
        return normalized;
      }
      if (typeof value === "object") {
        return Object.keys(value)
          .sort()
          .reduce((acc, key) => {
            acc[key] = normalizeValue(value[key]);
            return acc;
          }, {});
      }
      return value;
    };

    const stableStringify = (value) => {
      if (value === undefined) return "";
      return JSON.stringify(value);
    };

    const getFieldLabels = (before, after) => {
      if (!after) return [];
      const keys = Object.keys(after).filter((key) => !hiddenFields.has(key));
      const labels = keys
        .filter((key) => {
          if (!before) return true;
          const beforeValue = normalizeValue(before[key]);
          const afterValue = normalizeValue(after[key]);
          return stableStringify(beforeValue) !== stableStringify(afterValue);
        })
        .map((key) => fieldLabelMap[key] || key.replace(/_/g, " "))
        .map((label) => label.charAt(0).toUpperCase() + label.slice(1));
      if (labels.length) return labels;
      if (!before) return labels;
      const beforeNormalized = normalizeValue(before);
      const afterNormalized = normalizeValue(after);
      if (stableStringify(beforeNormalized) !== stableStringify(afterNormalized)) {
        return keys
          .map((key) => fieldLabelMap[key] || key.replace(/_/g, " "))
          .map((label) => label.charAt(0).toUpperCase() + label.slice(1));
      }
      return labels;
    };
    const isForecastItem = (item) => {
      if (item.entity !== "production_report") return false;
      const kind = String(item.after?.kind || "").toLowerCase();
      return kind === "forecast";
    };
    const normalizeItem = (item) => {
      const isForecast = isForecastItem(item);
      const entityLabel =
        item.entity === "management_person"
          ? "Company Profile"
          : isForecast
          ? "Forecast"
          : entityLabels[item.entity] || item.entity;
      const fields =
        item.entity === "company_profile" ? [] : getFieldLabels(item.before, item.after);
      return {
        ...item,
        key: `${item.entity}-${item.id}`,
        title: item.title || (isForecast ? "Forecast" : entityLabel),
        actionLabel: actionLabels[item.action] || item.action,
        entityLabel,
        fields,
      };
    };

    const isNewCompanyProfile = (item) => {
      if (item.entity !== "company_profile") return false;
      if (item.action === "create") return true;
      const title = `${item.title || ""}`.toLowerCase();
      return title.includes("new company profile");
    };
    const isEmptyManagementProfile = (item) => {
      if (item.entity !== "management_person") return false;
      if (item.action !== "create") return false;
      const after = item.after || {};
      const empty = (value) => value === null || value === undefined || `${value}`.trim() === "";
      return (
        empty(after.executive_name) &&
        empty(after.background_summary) &&
        empty(after.tenure_start_year) &&
        empty(after.estimated_lower_bound_start_year) &&
        empty(after.estimated_upper_bound_start_year)
      );
    };
    const profileItems = items
      .filter((item) => item.entity === "company_profile" || item.entity === "management_person")
      .filter((item) => !isNewCompanyProfile(item) && !isEmptyManagementProfile(item))
      .map(normalizeItem);

    const assetNameById = new Map(
      (assets || []).map((asset) => [asset.id, asset.asset_name || `Asset ${asset.id}`])
    );
    const forecastNameById = new Map(
      (forecasts || []).map((forecast) => [
        forecast.id,
        forecast.name || `Forecast ${forecast.id}`,
      ])
    );
    const resourceReserveById = new Map();
    Object.values(resourceReservesByAsset || {}).forEach((reserves) => {
      (reserves || []).forEach((reserve) => {
        resourceReserveById.set(reserve.id, reserve);
      });
    });

    const assetItems = items.filter((item) => item.entity === "asset").map(normalizeItem);
    const reserveItems = items
      .filter((item) => item.entity === "resource_reserve")
      .map(normalizeItem);
    const reportItemsRaw = items.filter(
      (item) => item.entity === "resource_reserve_report"
    );
    const reportItems = reportItemsRaw.map(normalizeItem);
    const valueItems = items
      .filter((item) => item.entity === "resource_reserve_value")
      .map(normalizeItem);
    const productionItems = items.filter((item) => item.entity === "production_report");
    const forecastItems = productionItems.filter(isForecastItem).map(normalizeItem);
    const actualProductionItems = productionItems
      .filter((item) => !isForecastItem(item))
      .map(normalizeItem);

    const assetGroupMap = new Map();
    const reportById = new Map(
      reportItemsRaw.map((item) => [
        item.id,
        {
          resourceReserveId:
            item.resource_reserve_id || item.after?.resource_reserve_id || null,
          title: item.title || entityLabels.resource_reserve_report,
        },
      ])
    );

    const ensureAssetGroup = (assetId) => {
      const resolvedAssetId = assetId ?? "unassigned";
      if (!assetGroupMap.has(resolvedAssetId)) {
        assetGroupMap.set(resolvedAssetId, {
          assetId: resolvedAssetId,
          assetItem: assetItems.find((item) => item.id === assetId) || null,
          resources: [],
          resourceReports: [],
          productionReports: [],
        });
      }
      return assetGroupMap.get(resolvedAssetId);
    };

    assetItems.forEach((item) => {
      ensureAssetGroup(item.id);
    });

    reserveItems.forEach((item) => {
      const assetId =
        item.asset_id || item.after?.asset_id || resourceReserveById.get(item.id)?.asset;
      const group = ensureAssetGroup(assetId);
      group.resources.push({
        ...item,
        reports: [],
      });
    });

    reportItems.forEach((item) => {
      const assetId =
        item.asset_id ||
        item.after?.asset_id ||
        resourceReserveById.get(item.resource_reserve_id)?.asset;
      const group = ensureAssetGroup(assetId);
      const resourceId = item.resource_reserve_id || item.after?.resource_reserve_id;
      if (resourceId) {
        const resourceGroup =
          group.resources.find((resource) => resource.id === resourceId) ||
          (() => {
            const reserve = resourceReserveById.get(resourceId);
            const placeholder = {
              key: `resource_reserve-${resourceId}`,
              id: resourceId,
              title: reserve?.name || `Resource ${resourceId}`,
              action: "update",
              actionLabel: actionLabels.update,
              entityLabel: entityLabels.resource_reserve,
              reports: [],
            };
            group.resources.push(placeholder);
            return placeholder;
          })();
        resourceGroup.reports.push({ ...item, estimates: [] });
      } else {
        group.resourceReports.push(item);
      }
    });

    valueItems.forEach((item) => {
      const reportId =
        item.resource_reserve_report_id || item.report_id || item.after?.report_id;
      if (!reportId) return;
      const reportMeta = reportById.get(reportId);
      const resourceId =
        item.resource_reserve_id ||
        item.after?.resource_reserve_id ||
        reportMeta?.resourceReserveId;
      const assetId = item.asset_id || item.after?.asset_id;
      const group = ensureAssetGroup(assetId);
      if (resourceId) {
        const resourceGroup =
          group.resources.find((resource) => resource.id === resourceId) ||
          (() => {
            const reserve = resourceReserveById.get(resourceId);
            const placeholder = {
              key: `resource_reserve-${resourceId}`,
              id: resourceId,
              title: reserve?.name || `Resource ${resourceId}`,
              action: "update",
              actionLabel: actionLabels.update,
              entityLabel: entityLabels.resource_reserve,
              reports: [],
            };
            group.resources.push(placeholder);
            return placeholder;
          })();
        let reportGroup = resourceGroup.reports.find((report) => report.id === reportId);
        if (!reportGroup) {
          reportGroup = {
            key: `resource_reserve_report-${reportId}`,
            id: reportId,
            title: reportMeta?.title || `Resource report ${reportId}`,
            action: "update",
            actionLabel: actionLabels.update,
            entityLabel: entityLabels.resource_reserve_report,
            estimates: [],
          };
          resourceGroup.reports.push(reportGroup);
        }
        reportGroup.estimates.push(item);
      } else {
        group.resourceReports.push(item);
      }
    });
    actualProductionItems.forEach((item) => {
      const group = ensureAssetGroup(item.asset_id || item.after?.asset_id);
      group.productionReports.push(item);
    });

    const forecastGroupMap = new Map();
    const ensureForecastGroup = (forecastId, forecastName) => {
      const resolvedForecastId = forecastId ?? (forecastName ? `name:${forecastName}` : "unassigned");
      if (!forecastGroupMap.has(resolvedForecastId)) {
        forecastGroupMap.set(resolvedForecastId, {
          forecastId: resolvedForecastId,
          title:
            resolvedForecastId === "unassigned"
              ? "Unassigned Forecast"
              : forecastNameById.get(resolvedForecastId) ||
                forecastName ||
                `Forecast ${resolvedForecastId}`,
          items: [],
        });
      }
      return forecastGroupMap.get(resolvedForecastId);
    };

    forecastItems.forEach((item) => {
      const forecastId =
        item.after?.forecast_id ||
        item.after?.forecast?.id ||
        item.after?.forecast ||
        item.before?.forecast_id ||
        item.before?.forecast?.id ||
        item.before?.forecast;
      const forecastName =
        item.after?.forecast_name ||
        item.after?.forecast?.name ||
        item.before?.forecast_name ||
        item.before?.forecast?.name ||
        null;
      const group = ensureForecastGroup(forecastId, forecastName);
      const assetId = item.asset_id || item.after?.asset_id;
      const assetName =
        assetId && assetNameById.get(assetId) ? assetNameById.get(assetId) : "Asset";
      group.items.push({
        ...item,
        title: item.title || `${assetName} forecast report`,
      });
    });

    const resolveForecastAction = (itemsForForecast) => {
      if (!itemsForForecast.length) return "update";
      const actions = new Set(itemsForForecast.map((entry) => entry.action));
      if (actions.has("delete")) return "delete";
      if (actions.has("create")) return "create";
      return "update";
    };

    const assetGroups = Array.from(assetGroupMap.values()).map((group) => {
      const assetTitle =
        group.assetItem?.title ||
        (group.assetId === "unassigned"
          ? "Unassigned Asset"
          : assetNameById.get(group.assetId) || `Asset ${group.assetId}`);
      return {
        ...group,
        assetTitle,
      };
    });

    return {
      entityLabels,
      profileItems,
      assetGroups,
      forecastGroups: Array.from(forecastGroupMap.values()).map((group) => ({
        ...group,
        action: resolveForecastAction(group.items),
      })),
    };
  }, [submissionPayload?.items, assets, forecasts, resourceReservesByAsset]);

  const editingResourceReportData = useMemo(() => {
    if (!resourceReportForm.report_id || !resourceReportForm.resource_reserve_id) return null;
    const reserveReports =
      resourceReportsByReserve[resourceReportForm.resource_reserve_id] || [];
    return (
      reserveReports.find((report) => report.id === resourceReportForm.report_id) || null
    );
  }, [resourceReportForm.report_id, resourceReportForm.resource_reserve_id, resourceReportsByReserve]);

  const resourceReserveNameById = useMemo(() => {
    const map = {};
    Object.values(resourceReservesByAsset || {}).forEach((reserves) => {
      (reserves || []).forEach((reserve) => {
        if (!reserve?.id) return;
        map[reserve.id] = reserve.name || "Resources & Reserves";
      });
    });
    return map;
  }, [resourceReservesByAsset]);

  const submissionSummary = useMemo(() => {
    const items = submissionPayload?.items || [];
    if (!items.length) return null;
    const isNewCompanyProfile = (item) => {
      if (item.entity !== "company_profile") return false;
      if (item.action === "create") return true;
      const title = `${item.title || ""}`.toLowerCase();
      return title.includes("new company profile");
    };
    const isEmptyManagementProfile = (item) => {
      if (item.entity !== "management_person") return false;
      if (item.action !== "create") return false;
      const after = item.after || {};
      const empty = (value) => value === null || value === undefined || `${value}`.trim() === "";
      return (
        empty(after.executive_name) &&
        empty(after.background_summary) &&
        empty(after.tenure_start_year) &&
        empty(after.estimated_lower_bound_start_year) &&
        empty(after.estimated_upper_bound_start_year)
      );
    };
    const isForecastItem = (item) =>
      item.entity === "production_report" && item.after?.kind === "forecast";
    const forecastKeys = new Set();
    const counts = items.reduce(
      (acc, item) => {
        if (isNewCompanyProfile(item) || isEmptyManagementProfile(item)) {
          return acc;
        }
        if (item.entity !== "resource_reserve_value" && item.entity !== "management_person")
          acc.total += 1;
        if (item.entity === "company_profile" || item.entity === "management_person")
          acc.profile += 1;
        if (item.entity === "asset") acc.assets += 1;
        if (item.entity === "resource_reserve") acc.resources += 1;
        if (item.entity === "resource_reserve_report") acc.resourceReports += 1;
        if (item.entity === "production_report" && !isForecastItem(item))
          acc.productionReports += 1;
        if (isForecastItem(item)) {
          const forecastKey =
            item.after?.forecast_id ||
            item.after?.forecast?.id ||
            item.after?.forecast ||
            item.before?.forecast_id ||
            item.before?.forecast?.id ||
            item.before?.forecast ||
            item.after?.forecast_name ||
            item.before?.forecast_name ||
            "unassigned";
          forecastKeys.add(String(forecastKey));
        }
        return acc;
      },
      {
        total: 0,
        profile: 0,
        assets: 0,
        resources: 0,
        resourceReports: 0,
        productionReports: 0,
        forecasts: 0,
      }
    );
    counts.forecasts = forecastKeys.size;
    if (counts.forecasts > 0) {
      const forecastItemsCount = items.filter(isForecastItem).length;
      counts.total = counts.total - forecastItemsCount + counts.forecasts;
    }
    const lines = [];
    if (counts.profile)
      lines.push(`${counts.profile} Profile Change${counts.profile === 1 ? "" : "s"}`);
    if (counts.assets) lines.push(`${counts.assets} Asset${counts.assets === 1 ? "" : "s"}`);
    if (counts.resources)
      lines.push(
        `${counts.resources} Resources & Reserves${counts.resources === 1 ? "" : ""}`
      );
    if (counts.resourceReports)
      lines.push(
        `${counts.resourceReports} Resource Report${
          counts.resourceReports === 1 ? "" : "s"
        }`
      );
    if (counts.productionReports)
      lines.push(
        `${counts.productionReports} Production Report${
          counts.productionReports === 1 ? "" : "s"
        }`
      );
    if (counts.forecasts)
      lines.push(`${counts.forecasts} Forecast${counts.forecasts === 1 ? "" : "s"}`);
    return {
      total: counts.total,
      lines,
    };
  }, [submissionPayload?.items]);

  const renderActionChip = (action, entityLabel) => {
    const chipText = (() => {
      if (action === "create") return `🆕 New ${entityLabel}`;
      if (action === "update") return `✏️ Updated ${entityLabel}`;
      if (action === "delete") return `🗑 Removed ${entityLabel}`;
      return `${action} ${entityLabel}`;
    })();
    const sxByAction = (() => {
      if (action === "create") {
        return {
          bgcolor: "rgba(34, 197, 94, 0.12)",
          color: "#166534",
          border: "1px solid rgba(34, 197, 94, 0.2)",
        };
      }
      if (action === "update") {
        return {
          bgcolor: "rgba(245, 158, 11, 0.15)",
          color: "#92400e",
          border: "1px solid rgba(245, 158, 11, 0.25)",
        };
      }
      if (action === "delete") {
        return {
          bgcolor: "transparent",
          color: "#b91c1c",
          border: "1px solid rgba(239, 68, 68, 0.4)",
        };
      }
      return {
        bgcolor: "rgba(15, 23, 42, 0.06)",
        color: "text.secondary",
        border: "1px solid rgba(148, 163, 184, 0.3)",
      };
    })();

    return (
      <Box component="span" sx={{ ...actionChipSx, ...sxByAction }}>
        {chipText}
      </Box>
    );
  };

  useEffect(() => {
    let active = true;
    const checkAccess = async () => {
      try {
        await loadCompany();
      } catch {
        if (!active) return;
        navigate("/access-denied", { replace: true });
      }
    };
    checkAccess();
    return () => {
      active = false;
    };
  }, [loadCompany, navigate]);

  useEffect(() => {
    if (!company?.default_reporting_currency) return;
    setSettingsCurrency(company.default_reporting_currency);
  }, [company?.default_reporting_currency]);

  const handleSaveSettings = async () => {
    if (!company?.slug) return;
    setSettingsSaveState({ loading: true, error: null, success: null });
    try {
      await companyApi.updateCompany(company.slug, {
        default_reporting_currency: settingsCurrency,
      });
      setCompany((prev) =>
        prev ? { ...prev, default_reporting_currency: settingsCurrency } : prev
      );
      setSettingsSaveState({
        loading: false,
        error: null,
        success: "Settings saved.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to save settings.";
      setSettingsSaveState({ loading: false, error: detail, success: null });
    }
  };

  useEffect(() => {
    if (!settingsSaveState.success) return;
    const timer = setTimeout(
      () => setSettingsSaveState((prev) => ({ ...prev, success: null })),
      4000
    );
    return () => clearTimeout(timer);
  }, [settingsSaveState.success]);

  useEffect(() => {
    if (!deleteFeedback) return;
    const timer = setTimeout(() => setDeleteFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [deleteFeedback]);

  const handleToggleAsset = (assetId) => (_event, isExpanded) => {
    setExpandedAssetId(isExpanded ? assetId : null);
  };

  // Scenario edits now occur inside the report dialog.

  const handleOpenSubmissionReview = async () => {
    if (!companyId) return;
    setSubmissionActionError(null);
    setSubmissionActionLoading(true);
    try {
      const data = await companyApi.submitCompanySubmission(companyId, { preview: true });
      setSubmissionPayload(data.payload);
      setSubmissionReviewOpen(true);
    } catch (err) {
      setSubmissionActionError(
        err?.response?.data?.detail || "Unable to prepare submission."
      );
    } finally {
      setSubmissionActionLoading(false);
    }
  };

  const handleSubmitSubmission = async () => {
    if (!companyId) return;
    setSubmissionActionError(null);
    setSubmissionActionLoading(true);
    try {
      await companyApi.submitCompanySubmission(companyId);
      setSubmissionReviewOpen(false);
      setSubmissionPayload(null);
      await loadSubmissionState();
    } catch (err) {
      setSubmissionActionError(
        err?.response?.data?.detail || "Unable to submit changes for review."
      );
    } finally {
      setSubmissionActionLoading(false);
    }
  };

  const handleCancelSubmission = async () => {
    if (!companyId) return;
    setSubmissionActionError(null);
    setSubmissionActionLoading(true);
    try {
      await companyApi.submitCompanySubmission(companyId, { cancel: true });
      setSubmissionCancelOpen(false);
      setSubmissionPayload(null);
      setSubmissionReviewOpen(false);
      await loadSubmissionState();
    } catch (err) {
      setSubmissionActionError(
        err?.response?.data?.detail || "Unable to cancel submission."
      );
    } finally {
      setSubmissionActionLoading(false);
    }
  };

  const handleCloseSubmissionReview = () => {
    setSubmissionReviewOpen(false);
  };


  if (!company) {
    return (
      <Container sx={{ mt: { xs: 1, md: 1 }, mb: { xs: 5, md: 8 }, px: { xs: 1.25, sm: 3 } }}>
        <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 2, md: 4 } }}>
          <Stack spacing={2}>
            <Skeleton variant="text" width="35%" height={36} />
            <Skeleton variant="text" width="70%" height={22} />
            <Skeleton variant="text" width="30%" height={18} />
            <Divider />
            <Stack direction="row" spacing={2}>
              <Skeleton variant="rounded" width={140} height={36} />
              <Skeleton variant="rounded" width={180} height={36} />
              <Skeleton variant="rounded" width={120} height={36} />
            </Stack>
            <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Skeleton variant="text" width="30%" height={24} />
                <Skeleton variant="rounded" width="100%" height={160} />
                <Skeleton variant="rounded" width="100%" height={160} />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: { xs: 1, md: 1 }, mb: { xs: 5, md: 8 }, px: { xs: 1.25, sm: 3 } }}>
      <Button
        component={RouterLink}
        to={`/companies/${slug}`}
        startIcon={<ArrowBackIcon />}
        variant="text"
        sx={{ mb: 1 }}
      >
        Back to Public Profile
      </Button>
      {showSubmissionBanner && (
        <Box sx={{ position: "sticky", top: 8, zIndex: 2, mb: 2 }}>
          <Alert
            severity={openSubmission ? "info" : "warning"}
            sx={{
              alignItems: "center",
              "& .MuiAlert-message": { py: 0 },
              "& .MuiAlert-action": { alignItems: "center", mt: 0 },
            }}
            action={
              openSubmission ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setSubmissionPayload(openSubmission.payload);
                      setSubmissionReviewOpen(true);
                    }}
                  >
                    View submission
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    disabled={submissionActionLoading}
                    onClick={() => setSubmissionCancelOpen(true)}
                  >
                    Cancel submission
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  disabled={!draftState?.has_drafts || submissionActionLoading}
                  onClick={handleOpenSubmissionReview}
                  sx={{ fontWeight: 700, textTransform: "none" }}
                >
                  Review & Submit
                </Button>
              )
            }
          >
            {openSubmission
              ? "Changes are pending review. Check your email for notifications on your submission. Cancel to continue editing."
              : "You have unpublished changes."}
          </Alert>
        </Box>
      )}


      <Dialog
        open={submissionReviewOpen}
        onClose={handleCloseSubmissionReview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Review changes</DialogTitle>
        <DialogContent dividers>
          {submissionActionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submissionActionError}
            </Alert>
          )}
          {groupedSubmissionItems ? (
            <Stack spacing={2}>
              {submissionSummary && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "rgba(15, 23, 42, 0.04)",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    {`You’re submitting ${submissionSummary.total} item${
                      submissionSummary.total === 1 ? "" : "s"
                    } for review`}
                  </Typography>
                  {submissionSummary.lines.length > 0 && (
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {submissionSummary.lines.map((line) => (
                        <Typography key={line} variant="caption" color="text.secondary">
                          {line}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
              {groupedSubmissionItems.profileItems.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "text.secondary",
                    }}
                  >
                    Profile Changes
                  </Typography>
                  <Stack spacing={1.25}>
                    {groupedSubmissionItems.profileItems.map((item) => (
                      <Box
                        key={item.key}
                        sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)", pb: 1 }}
                      >
                        <Typography variant="subtitle2" fontWeight={400}>
                          {item.title}
                          {renderActionChip(item.action, item.entityLabel)}
                        </Typography>
                        {item.action === "update" && (item.fields?.length ?? 0) > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {`Fields: ${item.fields.join(", ")}`}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {groupedSubmissionItems.assetGroups.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "text.secondary",
                    }}
                  >
                    Assets
                  </Typography>
                  <Stack spacing={1.5}>
                    {groupedSubmissionItems.assetGroups.map((assetGroup) => (
                      <Box key={assetGroup.assetId} sx={{ pl: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {assetGroup.assetTitle}
                          {renderActionChip(
                            assetGroup.assetItem?.action || "update",
                            groupedSubmissionItems.entityLabels?.asset || "Asset"
                          )}
                        </Typography>
                        {assetGroup.assetItem?.action === "update" &&
                          assetGroup.assetItem?.fields?.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {`Fields: ${assetGroup.assetItem.fields.join(", ")}`}
                            </Typography>
                          )}

                        {(assetGroup.resources.length > 0 || assetGroup.resourceReports.length > 0) && (
                          <Box sx={{ mt: 1, pl: 2 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{
                                display: "block",
                                mb: 0.75,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "text.secondary",
                              }}
                            >
                              Resources & Reserves
                            </Typography>
                            <Stack spacing={1.25}>
                              {assetGroup.resources.map((resource) => (
                                <Box key={resource.key}>
                                  <Typography variant="subtitle2" fontWeight={400}>
                                    {resource.title}
                                    {renderActionChip(resource.action, resource.entityLabel)}
                                  </Typography>
                                  {resource.action === "update" && (resource.fields?.length ?? 0) > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {`Fields: ${resource.fields.join(", ")}`}
                                    </Typography>
                                  )}
                                  {resource.reports.length > 0 && (
                                    <Box sx={{ mt: 0.75, pl: 2 }}>
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{
                                          display: "block",
                                          mb: 0.5,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.08em",
                                          color: "text.secondary",
                                        }}
                                      >
                                        Resource Estimate Reports
                                      </Typography>
                                      <Stack spacing={1}>
                                        {resource.reports.map((report) => (
                                          <Box key={report.key}>
                                            <Typography variant="subtitle2" fontWeight={400}>
                                              {report.title}
                                              {renderActionChip(report.action, report.entityLabel)}
                                            </Typography>
                                            {report.action === "update" && (report.fields?.length ?? 0) > 0 && (
                                              <Typography variant="caption" color="text.secondary">
                                                {`Fields: ${report.fields.join(", ")}`}
                                              </Typography>
                                            )}
                                          </Box>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                              {assetGroup.resourceReports.map((report) => (
                                <Box key={report.key}>
                                  <Typography variant="subtitle2" fontWeight={400}>
                                    {report.title}
                                    {renderActionChip(report.action, report.entityLabel)}
                                  </Typography>
                                  {report.action === "update" && (report.fields?.length ?? 0) > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {`Fields: ${report.fields.join(", ")}`}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {assetGroup.productionReports.length > 0 && (
                          <Box sx={{ mt: 1.5, pl: 2 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{
                                display: "block",
                                mb: 0.75,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "text.secondary",
                              }}
                            >
                              Production Reports
                            </Typography>
                            <Stack spacing={1.25}>
                              {assetGroup.productionReports.map((report) => (
                                <Box key={report.key}>
                                  <Typography variant="subtitle2" fontWeight={400}>
                                    {report.title}
                                    {renderActionChip(report.action, report.entityLabel)}
                                  </Typography>
                                  {report.action === "update" && (report.fields?.length ?? 0) > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {`Fields: ${report.fields.join(", ")}`}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {groupedSubmissionItems.forecastGroups?.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "text.secondary",
                    }}
                  >
                    Forecasts
                  </Typography>
                  <Stack spacing={1.5}>
                    {groupedSubmissionItems.forecastGroups.map((forecastGroup) => (
                      <Box key={`forecast-group-${forecastGroup.forecastId}`} sx={{ pl: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {forecastGroup.title}
                          {renderActionChip(
                            forecastGroup.action,
                            groupedSubmissionItems.entityLabels?.forecast || "Forecast"
                          )}
                        </Typography>
                        {forecastGroup.items.length > 0 &&
                          forecastGroup.items.some(
                            (item) => item.action === "update" && (item.fields?.length ?? 0) > 0
                          ) && (
                            <Typography variant="caption" color="text.secondary">
                              {`Fields: ${Array.from(
                                new Set(
                                  forecastGroup.items.flatMap((item) => item.fields || [])
                                )
                              ).join(", ")}`}
                            </Typography>
                          )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No changes to submit.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmissionReview}>Close</Button>
          {!openSubmission && (
            <Button
              variant="contained"
              onClick={handleSubmitSubmission}
              disabled={submissionActionLoading || !submissionPayload?.items?.length}
            >
              {submissionActionLoading ? "Submitting..." : "Submit for review"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={submissionCancelOpen}
        onClose={() => setSubmissionCancelOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancel submission?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will cancel the pending submission and return all items to draft so you can
            continue editing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmissionCancelOpen(false)}>Keep submission</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubmission}
            disabled={submissionActionLoading}
          >
            Cancel submission
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          ...theme.mixins.frostedBox,
          p: { xs: 2, md: 4 },
          width: "100%",
          maxWidth: { xs: "100%", md: "100%" },
          mx: "auto",
        }}
      >
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>
            Company Management Workspace
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{maxWidth:"80%"}}>
            This workspace is where you manage your company’s profile, assets, operational data and production forecasts. All edits are saved as drafts and are not visible publicly until they are submitted for review and approved.
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Edit → Review → Submit → Publish
          </Typography>
        </Stack>

        <Box sx={{ mt: 2, mb: 3 }}>
          <Divider />
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            borderBottom: "1px solid rgba(255,255,255,0.3)",
            mb: 2.5,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minWidth: 0,
              px: { xs: 1, sm: 2 },
              whiteSpace: "nowrap",
            },
          }}
        >
          <Tab label="Profile" />
          <Tab label="Assets & Operations" />
          <Tab label="Forecasts" />
          <Tab label="Settings" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 0.25, md: 3 }, height: "100%" }}>
            <SummaryTab
              mode="manage"
              company={company}
              canEditCompany={canEditDrafts}
              onCompanyUpdated={loadCompany}
            />
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 2, md: 3 }, height: "100%" }}>
            <Stack spacing={{ xs: 2, sm: 1.5 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 1.5 }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
               

                <Box>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 1, sm: 1.5 }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Assets & Operations
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setAssetDialogRequest({ mode: "create" })}
                      disabled={!canEditDrafts}
                      fullWidth={isMobile}
                      size={isMobile ? "small" : "medium"}
                    >
                      Add Asset
                    </Button>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: { xs: "100%", sm: "80%" }, mb: 1 }}
                  >
                    Assets are a general term for a mining company’s projects, properties, and complexes.
                    Within each asset, companies have mines and deposits, which ResourceNAV calls
                    "Resources & Reserves."
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: { xs: "100%", sm: "80%" }, mb: 1 }}
                  >
                    All data (Resource & Reserve Estimates, Production Reporting, and Forecasts) are
                    captured at the mine & deposit level and then rolled up to the Asset and Company level to
                    produce the numbers seen in the Assets, Operations and Forecast tabs here.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: { xs: "100%", sm: "80%" }, mb: 1 }}
                  >
                    Changes are saved as drafts and must be submitted for review before being published
                    publicly.
                  </Typography>
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      backgroundColor: "transparent",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="small" />}
                      sx={{
                        px: { xs: 0.5, sm: 1 },
                        py: { xs: 0.75, sm: 1 },
                        minHeight: "auto",
                        "& .MuiAccordionSummary-content": { m: 0 },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InfoOutlinedIcon fontSize="small" />
                        <Typography variant="subtitle2">
                          Definitions
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
                      <Stack spacing={0.75}>
                        <Stack spacing={0.6}>
                          <Typography variant="body2" color="text.secondary">
                            • <b>Assets:</b> projects/properties/complexes owned or operated by the company
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            • <b>Resources & Reserves:</b> mines and deposits within each asset
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            • <b>Resource & Reserve Estimates:</b> point-in-time metal amounts by classification (Proven,
                            Probable, Measured, Indicated, Inferred)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            • <b>Production Reporting:</b> reported metrics by period
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            • <b>Forecasts:</b> expected future production by period
                          </Typography>
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Box>
                  
                  
                  <Divider sx={{ mb: { xs: 2, sm: 3 } }} />
              </Stack>

              {!canEditDrafts && (
                <Alert severity="info">
                  {openSubmission
                    ? "A submission is pending review. Cancel it before making edits."
                    : "An approved company claim is required before you can submit asset updates."}
                </Alert>
              )}

              <AssetDialogs
                companyApi={companyApi}
                companyId={companyId}
                canEditCompany={canEditDrafts}
                defaultReportingCurrency={defaultReportingCurrency}
                onAssetSaved={handleAssetSaved}
                onAssetDeleted={handleAssetDeleted}
                assetDialogRequest={assetDialogRequest}
                onAssetDialogRequestHandled={() => setAssetDialogRequest(null)}
                assetDeleteRequest={assetDeleteRequest}
                onAssetDeleteRequestHandled={() => setAssetDeleteRequest(null)}
                onDeleteFeedback={setDeleteFeedback}
              />
              {deleteFeedback && <Alert severity="info">{deleteFeedback}</Alert>}
              {resourceReportDeleteState && (
                <Alert severity="info">{resourceReportDeleteState}</Alert>
              )}
              {resourceReserveDeleteState && (
                <Alert severity="info">{resourceReserveDeleteState}</Alert>
              )}

              <Divider />

              {assetsLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading assets...
                </Typography>
              ) : assetsError ? (
                <Alert severity="error">{assetsError}</Alert>
              ) : assets.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No assets have been added yet.
                </Typography>
              ) : (
                <Stack spacing={{ xs: 2, sm: 1.5 }}>
                  {assets.map((asset) => {
                    const reserves = resourceReservesByAsset[asset.id] || [];
                    const assetDeleteRequested = asset.status === "delete_requested";
                    const productionReportsByReserveForAsset = {};
                    (reserves || []).forEach((reserve) => {
                      productionReportsByReserveForAsset[reserve.id] =
                        productionReportsByReserve[reserve.id] || [];
                    });
                    return (
                      <Accordion
                        key={asset.id}
                        expanded={expandedAssetId === asset.id}
                        onChange={handleToggleAsset(asset.id)}
                        sx={{
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: 1.5,
                          backgroundColor: assetDeleteRequested
                            ? "rgba(15, 23, 42, 0.06)"
                            : "rgba(34, 35, 37, 0.02)",
                          opacity: assetDeleteRequested ? 0.62 : 1,
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          component="div"
                          sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 0.75, sm: 0.5 },
                            "& .MuiAccordionSummary-expandIconWrapper": {
                              mr: 1,
                            },
                          }}
                        >
                          <Stack
                            direction="column"
                            spacing={1.5}
                            justifyContent="space-between"
                            alignItems="stretch"
                            sx={{ width: "100%" }}
                          >
                            <Box sx={{ pl: 2 }}>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={{ xs: 0.75, sm: 1 }}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", sm: "center" }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mt: 0.25, flexWrap: "wrap" }}
                                  >
                                    <Typography variant="subtitle1" fontWeight={700}>
                                      {asset.asset_name}
                                    </Typography>
                                     <Chip
                                        size="small"
                                        variant="outlined"
                                        {...getStatusChipProps(asset.status)}
                                      />
                                    {assetDeleteRequested && (
                                      <Typography variant="caption" color="error.main" fontWeight={700}>
                                        Pending deletion in next submission
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        Last updated: {formatLastUpdated(asset.last_updated_at)}
                                      </Typography>                                   
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatJurisdictionLabel(asset.jurisdiction)}
                                  </Typography>
                                  <Stack spacing={0.35} sx={{ mt: 0.5, maxWidth: 820 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {`Ownership: ${
                                        asset.ownership_percentage !== null &&
                                        asset.ownership_percentage !== undefined
                                          ? `${formatNumber(asset.ownership_percentage)}%`
                                          : "N/A"
                                      } | Lifecycle: ${getLifecycleLabel(
                                        asset.lifecycle
                                      )} | Lifecycle Stage: ${getLifecycleStageLabel(
                                        asset.lifecycle_stage
                                      )}${
                                        asset.lifecycle === "development" ||
                                        asset.lifecycle === "exploration"
                                          ? asset.expected_first_production_year
                                            ? ` | Expected first production year: ${asset.expected_first_production_year}`
                                            : ""
                                          : asset.lifecycle === "production"
                                          ? asset.start_year_of_operation
                                            ? ` | Start year of operation: ${asset.start_year_of_operation}`
                                            : ""
                                          : asset.lifecycle === "closure"
                                          ? asset.closure_or_reclamation_year
                                            ? ` | Closure/reclamation year: ${asset.closure_or_reclamation_year}`
                                            : ""
                                          : ""
                                      }`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {`Reporting currency: ${
                                        asset.reporting_currency || defaultReportingCurrency
                                      } | Land size (acres): ${
                                        asset.land_size_acres !== null &&
                                        asset.land_size_acres !== undefined
                                          ? formatNumberWithCommas(asset.land_size_acres)
                                          : "N/A"
                                      } | Land explored: ${
                                        asset.pct_land_explored !== null &&
                                        asset.pct_land_explored !== undefined
                                          ? `${formatNumber(asset.pct_land_explored)}%`
                                          : "N/A"
                                      }`}
                                    </Typography>
                                  </Stack>
                                </Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                  size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setAssetDialogRequest({ mode: "edit", asset });
                                    }}
                                    onFocus={(event) => event.stopPropagation()}
                                    disabled={!canEditDrafts || assetDeleteRequested}
                                    sx={{ minWidth: "auto", px: 0 }}
                                  >
                                    <EditIcon fontSize="small" />
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setAssetDeleteRequest(asset);
                                    }}
                                    onFocus={(event) => event.stopPropagation()}
                                    disabled={!canEditDrafts || assetDeleteRequested}
                                    sx={{ minWidth: "auto", px: 1.25 }}
                                  >
                                    <CloseIcon fontSize="small" />
                                </Button>
                              </Stack>
                            </Stack>
                            </Box>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
                          <Stack spacing={{ xs: 2, sm: 1.5 }}>
                            <Divider />
                            <ResourceSection
                              assetId={asset.id}
                              reserves={reserves}
                              resourceReportsByReserve={resourceReportsByReserve}
                              productionReportsByReserve={productionReportsByReserveForAsset}
                              isMobile={isMobile}
                              canEdit={canEditDrafts && !assetDeleteRequested}
                              onAddResource={handleOpenAddResourceReserve}
                              onEditResourceReserve={handleOpenEditResourceReserve}
                              onDuplicateResourceReserve={handleDuplicateResourceReserve}
                              onRequestDeleteResourceReserve={setResourceReserveDeleteTarget}
                              onAddReport={handleOpenAddResourceReport}
                              onEditReport={handleOpenEditResourceReport}
                              onDuplicateReport={handleDuplicateResourceReport}
                              onRequestDeleteReport={setResourceReportDeleteTarget}
                              onAddProductionReport={handleOpenAddProductionReport}
                              onEditProductionReport={handleOpenEditProductionReport}
                              onDuplicateProductionReport={handleDuplicateProductionReport}
                              onRequestDeleteProductionReport={setProductionReportDeleteTarget}
                            />
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Box>
        )}

        {tab === 2 && (
          <ForecastSection
            assets={assets}
            resourceReservesByAsset={resourceReservesByAsset}
            companyId={companyId}
            canEdit={canEditDrafts}
            commodities={commodities}
            productionMetrics={productionMetrics}
            defaultReportingCurrency={defaultReportingCurrency}
            forecasts={forecasts}
            loading={forecastsLoading}
            onForecastSaved={loadCompany}
          />
        )}


        {tab === 3 && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 2, md: 3 }, height: "100%" }}>
            <Stack spacing={2} sx={{ maxWidth: 520 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Used to pre-fill new assets and new reports. Conversions are not applied.
                </Typography>
              </Box>
              {settingsSaveState.error && (
                <Alert severity="error">{settingsSaveState.error}</Alert>
              )}
              {settingsSaveState.success && (
                <Alert severity="success">{settingsSaveState.success}</Alert>
              )}
              <TextField
                select
                label="Default reporting currency"
                value={settingsCurrency}
                onChange={(event) => setSettingsCurrency(event.target.value)}
                fullWidth
              >
                {CURRENCY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box>
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={!canEditCompany || settingsSaveState.loading}
                >
                  {settingsSaveState.loading ? "Saving..." : "Save settings"}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}

      </Box>

      <ResourceDialogs
        stageOptions={stageOptions}
        commodities={commodities}
        addResourceReserveOpen={addResourceReserveOpen}
        addResourceReportOpen={addResourceReportOpen}
        resourceReportDialogMode={resourceReportDialogMode}
        resourceReportDeleteTarget={resourceReportDeleteTarget}
        economicScenarioSaveState={economicScenarioSaveState}
        economicScenarioDeleteTarget={economicScenarioDeleteTarget}
        resourceReportScenarioReport={editingResourceReportData}
        resourceReportScenarioReserveId={resourceReportForm.resource_reserve_id || null}
        resourceReportDuplicateNotice={resourceReportDuplicateNotice}
        resourceReserveNameById={resourceReserveNameById}
        onDraftScenarioFieldChange={handleDraftScenarioFieldChange}
        onDraftScenarioPriceChange={handleDraftScenarioPriceChange}
        onAddDraftScenarioPriceRow={handleAddDraftScenarioPriceRow}
        onRemoveDraftScenarioPriceRow={handleRemoveDraftScenarioPriceRow}
        onRemoveDraftScenario={handleRemoveDraftScenario}
        resourceReserveForm={resourceReserveForm}
        resourceReportForm={resourceReportForm}
        resourceReserveSaveState={resourceReserveSaveState}
        resourceReportSaveState={resourceReportSaveState}
        resourceReserveDialogMode={resourceReserveDialogMode}
        resourceReserveDeleteTarget={resourceReserveDeleteTarget}
        classificationTable={classificationTable}
        onCloseResourceReserve={handleCloseResourceReserveDialog}
        onCancelDeleteResourceReserve={() => setResourceReserveDeleteTarget(null)}
        onConfirmDeleteResourceReserve={handleConfirmDeleteResourceReserve}
        onCloseResourceReport={handleCloseResourceReportDialog}
        onResourceReserveFieldChange={handleResourceReserveFieldChange}
        onResourceReportFieldChange={handleResourceReportFieldChange}
        onCommoditySelectionChange={handleCommoditySelectionChange}
        onClassificationRowChange={handleClassificationRowChange}
        onSubmitResourceReserve={handleSubmitResourceReserve}
        onUpdateResourceReserve={handleUpdateResourceReserve}
        onSubmitResourceReport={handleSubmitResourceReport}
        onUpdateResourceReport={handleUpdateResourceReport}
        onRequestDeleteResourceReport={setResourceReportDeleteTarget}
        onCancelDeleteResourceReport={() => setResourceReportDeleteTarget(null)}
        onConfirmDeleteResourceReport={handleConfirmDeleteResourceReport}
        onAddScenarioFromReport={handleOpenAddEconomicScenario}
        onEditScenarioFromReport={handleOpenEditEconomicScenario}
        onRequestDeleteScenarioFromReport={(payload) =>
          setEconomicScenarioDeleteTarget({
            ...payload,
            isDraft: Boolean(payload?.scenarioId?.startsWith?.("draft-")),
          })
        }
        onCancelDeleteEconomicScenario={() => setEconomicScenarioDeleteTarget(null)}
        onConfirmDeleteEconomicScenario={handleConfirmDeleteEconomicScenario}
        resourceReportScenarioDrafts={draftEconomicScenarios}
        resourceReportSourceTitleOptions={resourceReportSourceOptions.titles}
        resourceReportSourceUrlOptions={resourceReportSourceOptions.urls}
      />

      <ProductionDialogs
        addProductionReportOpen={addProductionReportOpen}
        productionReportDialogMode={productionReportDialogMode}
        productionReportDeleteTarget={productionReportDeleteTarget}
        productionReportForm={productionReportForm}
        productionReportSaveState={productionReportSaveState}
        productionReportDuplicateNotice={productionReportDuplicateNotice}
        resourceReserveNameById={resourceReserveNameById}
        productionMetrics={productionMetrics}
        productionMetricEntries={productionMetricEntries}
        productionMetricOptions={productionMetricOptions}
        showAddMetricSelector={showAddMetricSelector}
        commodities={commodities}
        productionReportSourceTitleOptions={productionReportSourceOptions.titles}
        productionReportSourceUrlOptions={productionReportSourceOptions.urls}
        onCloseProductionReport={handleCloseProductionReportDialog}
        onCancelDeleteProductionReport={() => setProductionReportDeleteTarget(null)}
        onConfirmDeleteProductionReport={handleConfirmDeleteProductionReport}
        onProductionReportFieldChange={handleProductionReportFieldChange}
        onSelectProductionMetric={handleSelectProductionMetric}
        onProductionMetricEntryChange={handleProductionMetricEntryChange}
        onRemoveProductionMetric={handleRemoveProductionMetric}
        onToggleAddMetricSelector={handleToggleAddMetricSelector}
        onSubmitProductionReport={submitProductionReport}
        onUpdateProductionReport={updateProductionReport}
      />

    </Container>
  );
}
